import type { StudentIdPrefixFilter } from '../types';

/** Default prefix length (first 3 digits of iş nömrəsi) */
export const PREFIX_LENGTH = 3;

export interface PrefixInfo {
  prefix: string;
  count: number;
}

/**
 * Extracts the 3-digit prefix from a student_id string.
 * Returns null if the student_id is too short or invalid.
 */
export function extractPrefix(studentId: string | number | null | undefined): string | null {
  if (studentId == null) return null;
  const str = String(studentId).trim();
  if (str.length < PREFIX_LENGTH) return null;
  return str.substring(0, PREFIX_LENGTH);
}

/**
 * Given an array of student objects with a student_id field,
 * returns sorted unique 3-digit prefixes with counts.
 */
export function extractUniquePrefixes(
  students: Array<{ student_id?: string | number; [key: string]: any }>
): PrefixInfo[] {
  const prefixMap = new Map<string, number>();

  for (const student of students) {
    const prefix = extractPrefix(student.student_id);
    if (prefix) {
      prefixMap.set(prefix, (prefixMap.get(prefix) || 0) + 1);
    }
  }

  return Array.from(prefixMap.entries())
    .map(([prefix, count]) => ({ prefix, count }))
    .sort((a, b) => a.prefix.localeCompare(b.prefix, undefined, { numeric: true }));
}

/**
 * Returns true if the student passes the prefix filter.
 * When filter is undefined, disabled, or has no prefixes, ALL students pass (backward compat).
 */
export function matchesPrefixFilter(
  studentId: string | number | null | undefined,
  filter: StudentIdPrefixFilter | undefined | null
): boolean {
  if (!filter || !filter.enabled || !filter.prefixes || filter.prefixes.length === 0) {
    return true;
  }
  const prefix = extractPrefix(studentId);
  if (!prefix) return false;
  return filter.prefixes.includes(prefix);
}

/**
 * Validates a StudentIdPrefixFilter before saving.
 * Returns error message string if invalid, null if valid.
 */
export function validatePrefixFilter(
  filter: StudentIdPrefixFilter | undefined | null
): string | null {
  if (!filter) return null;
  if (!filter.enabled) return null;
  if (!Array.isArray(filter.prefixes) || filter.prefixes.length === 0) {
    return 'Ən azı bir prefiks seçilməlidir';
  }
  for (const p of filter.prefixes) {
    if (typeof p !== 'string' || p.length !== PREFIX_LENGTH || !/^\d+$/.test(p)) {
      return `Keçərsiz prefiks: "${p}". Prefiks ${PREFIX_LENGTH} rəqəmdən ibarət olmalıdır.`;
    }
  }
  return null;
}
